import React from "react";
import { ArrowRight } from "lucide-react";
import PublicLayout, { Eyebrow, PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { C, SERIF } from "@/lib/publicLinks";
import { DEFAULT_ABOUT, useSiteContent } from "@/lib/siteContent";

export default function About() {
  const c = useSiteContent("about", DEFAULT_ABOUT);
  const { hero, founder, credentials, cta } = c;
  const creds = (credentials?.items || []).filter((x) => x.visible !== false);

  return (
    <PublicLayout>
      {hero?.visible !== false && (
        <section style={{ background: C.lightBg }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <Eyebrow>{hero.eyebrow}</Eyebrow>
            <h1 className="text-4xl sm:text-5xl leading-[1.1] text-slate-900 max-w-3xl" style={SERIF} data-testid="about-h1">{hero.headline}</h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl">{hero.subheadline}</p>
          </div>
        </section>
      )}

      {founder?.visible !== false && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>{founder.kicker}</div>
            <H2 className="mt-2">{founder.name}</H2>
            <p className="mt-2 text-sm text-slate-500">{founder.subtitle}</p>
            <div className="mt-6 space-y-4 text-slate-600">
              {(founder.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
              {founder.quote ? <blockquote className="pl-5 text-lg text-slate-800" style={{ ...SERIF, borderLeft: `3px solid ${C.accent}` }}>&ldquo;{founder.quote}&rdquo;</blockquote> : null}
            </div>
          </div>
          <div className="space-y-6">
            {founder.image ? (
              <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.line}`, aspectRatio: "3/4" }} data-testid="about-image">
                <img src={founder.image} alt={founder.name} className="w-full h-full object-cover" />
              </div>
            ) : null}
            {credentials?.visible !== false && (
              <div className="rounded-2xl p-6" style={{ background: C.lightBg }} data-testid="about-credentials">
                <div className="text-sm font-semibold uppercase tracking-widest" style={{ color: C.accent }}>{credentials.heading}</div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {creds.map((c2, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: C.accent }} /> {c2.label}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {cta?.visible !== false && (
        <section style={{ background: C.plum }} className="text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="text-3xl sm:text-4xl" style={SERIF}>{cta.heading}</h2>
            <p className="mt-4" style={{ color: "#D9CCEA" }}>{cta.copy}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <PrimaryBtn to={cta.primaryTo}>{cta.primaryLabel} <ArrowRight className="h-4 w-4" /></PrimaryBtn>
              <GhostBtn to={cta.secondaryTo} className="!bg-transparent !text-white">{cta.secondaryLabel}</GhostBtn>
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
